import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ANALYTICAL_VARIABLE_WRITE_MUTATION_KEY } from '@verantos-dev/core-constants';

import { projectPageKeys } from '../lib/api/queryKeys';

/**
 * Subscribes to analytical-variable write mutations (e.g. rename, OR-6090)
 * and invalidates the project artifact manifest + activity feed when one
 * completes, regardless of which sub-route is currently active.
 *
 * Mounted once in ProjectPage so it stays active across the full project
 * session. Mirrors `useProjectPageCohortSync` / `useProjectPageAnalyticalDatasetSync`:
 * the AV write mutations live in `feature-analytical-variables`, which this
 * lib can't import (build cycle), so the bridge is the mutation cache plus a
 * shared mutation-key tag from core-constants.
 */
export const useProjectPageAnalyticalVariableSync = (projectId: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        event.mutation.options.mutationKey?.[0] === ANALYTICAL_VARIABLE_WRITE_MUTATION_KEY
      ) {
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.artifactsForProject(projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: projectPageKeys.activityForProject(projectId),
        });
      }
    });
  }, [queryClient, projectId]);
};
